import { Chat, ChatMessageData, ChatRef, Loading, useStateWithRef, WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import React, { ForwardedRef, useEffect, useMemo, useRef, useState } from "react";

import { HeaderChat, PedagogyUtils } from "@kalyzee/escaet-ui";
import { isMeetingSessionRoleGreaterThan, MeetingChatService } from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { Chatroom, ChatroomStatus, MeetingSessionRole } from "@kalyzee/kast-web-sdk/websocket";
import { ReactComponent as IconChatDisabled } from "../../assets/icons/noChat.svg";
import { sdkClient } from "../../helpers/sdk";
import { toastError } from "../../helpers/toast";
import { useMeetingSelector } from "../../hooks/meeting";
import "./Chat.css";
import styles from "./Chat.module.css";

export interface DashboardChatProps extends WithNativeHTMLProps<HTMLDivElement> {
  chatroom: Chatroom;
  meetingContainer?: MeetingContainerAPI;
  className?: string;
  style?: React.CSSProperties;
  hideHeader?: boolean;
  hideActiveChatButton?: boolean;
  hideClearChatButton?: boolean;
  hideSendBar?: boolean;
  disableReactions?: boolean;
  disableDeletion?: boolean;
  inactive?: boolean;
  onUnreadMessages?: (messages: ChatMessageData[]) => void;
  onClick?: (message: ChatMessageData) => void;
}

export interface DashboardChatRef {}

const DashboardChat = React.forwardRef(
  (
    {
      chatroom,
      meetingContainer,
      className,
      style,
      nativeProps,
      hideHeader = false,
      hideActiveChatButton = false,
      hideClearChatButton = false,
      hideSendBar = false,
      disableReactions = false,
      disableDeletion = true,
      inactive,
      onUnreadMessages,
      onClick,
    }: DashboardChatProps,
    forwardRef: ForwardedRef<DashboardChatRef>
  ) => {
    const [chatStatus, setChatStatus] = useState<ChatroomStatus | "loading" | undefined>();
    const [chatIsReady, setChatIsReady, chatIsReadyRef] = useStateWithRef(false);
    const [meeting] = useMeetingSelector(meetingContainer, (state) => state.meeting);
    const [chatAdapters] = useMeetingSelector(meetingContainer, (state) => state.chatAdapters);
    const chatAdapter = useMemo(() => chatAdapters?.[chatroom.id], [chatAdapters, chatroom.id]);
    const chatRef = useRef<ChatRef>(null);

    const isUserFromPedagogy = (userId: string) => {
      if (!meeting) return false;
      const session = meeting.sessions?.find((s) => s.user?.id === userId);
      if (!session) return;
      return isMeetingSessionRoleGreaterThan(session.role, MeetingSessionRole.MODERATOR);
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      if (!chatAdapter) return;
      const stateListener = chatAdapter.addEventListener("state", (state) => setChatStatus(state));
      const readyListener = chatAdapter.addEventListener("ready", () => {
        if (!chatIsReadyRef.current) setChatIsReady(true);
      });
      if (chatAdapter.ready && !chatIsReadyRef.current) setChatIsReady(true);
      return () => {
        stateListener.flush();
        readyListener.flush();
      };
    }, [chatAdapter]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    if (!meeting || !chatAdapter) return null;

    const chatIsDisabled = () => {
      if (chatStatus) return chatStatus === ChatroomStatus.disabled;
      return chatroom?.status === ChatroomStatus.disabled;
    };

    const classes = [styles.container, "dashboard_chat"];
    if (hideSendBar) classes.push("hide_send_bar");
    if (className) classes.push(className);

    return (
      <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style}>
        { !hideHeader ? <HeaderChat
          conversationsLength={0}
          tagStyle={{ display: "none" }}
          isChatActive={!chatIsDisabled()}
          disabledActiveChatButton={hideActiveChatButton}
          disabledClearChatButton={hideClearChatButton}
          onToggleChatActive={async () => {
            if (!chatRef.current) return;
            const previousStatus = chatStatus;
            setChatStatus("loading");
            const response = chatIsDisabled()
              ? await MeetingChatService.enableChatroom(sdkClient, chatAdapter.getMeetingId(), chatAdapter.getChatroomId())
              : await MeetingChatService.disableChatroom(sdkClient, chatAdapter.getMeetingId(), chatAdapter.getChatroomId());
            if (response.error || !response.response) {
              setChatStatus(previousStatus);
              console.error("Une erreur est apparue: ", response.error);
              toastError("Une erreur est apparue.");
              return;
            }
            setChatStatus(response.response.state);
          }}
          onClearChat={() => {
            chatAdapter.archiveMessages();
          }}
          cx={styles.header}
        ></HeaderChat> : null}
        <div className={styles.content}>
          {chatIsReady ? (
            <>
              {chatroom ? (
                <>
                  <Chat
                    className={styles.chat}
                    ref={chatRef}
                    adapter={chatAdapter}
                    hideAvatarMaxRepetition={0}
                    disabled={chatIsDisabled()}
                    messageParameters={{
                      disableBubbleInteraction: disableReactions && disableDeletion,
                      disableDeletion: disableDeletion,
                      disableReaction: disableReactions,
                      disableAvatarInteraction: !onClick,
                      onAvatarClick: (msg) => {
                        onClick?.(msg);
                        return false;
                      },
                      onCustomValidationForDeletion: (msg) => true,
                    }}
                    sendBarProps={{
                      showIndicator: true,
                      hideIndicatorIfEmpty: true,
                      limit: 250,
                    }}
                    callbacks={{
                      unreadMessages: onUnreadMessages,
                    }}
                    inactive={inactive}
                    onCustomRenderUsername={(msg, _, username) => {
                      return (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          {isUserFromPedagogy(msg.user.id) ? <PedagogyUtils.HeaderChatMessage /> : null}
                          <div>{username}</div>
                        </div>
                      );
                    }}
                  />

                  {chatIsDisabled() && (
                    <div className={styles.overlayDisabled}>
                      <IconChatDisabled width={40} height={40} />
                    </div>
                  )}
                  {chatStatus === "loading" && (
                    <Loading style={{ width: "100%", height: "100%", backgroundColor: "#00000022", position: "absolute", top: "0px", left: "0px" }} />
                  )}
                </>
              ) : (
                <div>Le chat n'est pas disponible</div>
              )}
            </>
          ) : (
            <Loading style={{ width: "100%", height: "100%" }} />
          )}
        </div>
      </div>
    );
  }
);

export default React.memo(DashboardChat);
